/**
 * IPTV source management, with live sync progress.
 *
 * The "Test" button hits `/api/sources/test`, which saves nothing — so a mistyped
 * password does not leave a broken source behind. Only a source that answered gets
 * saved.
 *
 * Sync is a 202 + poll, because importing 150,000 items takes minutes and an open HTTP
 * request would time out at the reverse proxy long before it finished.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { SourceDto, SourceTestResult, SyncState } from '@filmodasi/shared';
import { api } from '../api/client';
import { useApi } from '../api/hooks';
import { ErrorBox, Spinner } from '../components/ui';
import { useConfirm } from '../components/Dialog';

type Draft =
  | { type: 'xtream'; name: string; baseUrl: string; username: string; password: string }
  | { type: 'm3u'; name: string; m3uUrl: string };

const emptyXtream: Draft = { type: 'xtream', name: '', baseUrl: '', username: '', password: '' };
const emptyM3u: Draft = { type: 'm3u', name: '', m3uUrl: '' };

export function SourcesPage() {
  const { data, error, loading, reload } = useApi<SourceDto[]>('/api/sources');
  const [draft, setDraft] = useState<Draft>(emptyXtream);
  const [test, setTest] = useState<SourceTestResult | null>(null);
  const [busy, setBusy] = useState<'test' | 'save' | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  async function runTest(e: FormEvent) {
    e.preventDefault();
    setBusy('test');
    setFormError(null);
    setTest(null);
    try {
      setTest(await api<SourceTestResult>('/api/sources/test', { method: 'POST', body: draft }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Test başarısız');
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    setBusy('save');
    setFormError(null);
    try {
      await api('/api/sources', { method: 'POST', body: draft });
      setDraft(draft.type === 'xtream' ? emptyXtream : emptyM3u);
      setTest(null);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Kaydedilemedi');
    } finally {
      setBusy(null);
    }
  }

  return (
    <main className="sources">
      <h1>Kaynaklar</h1>

      {loading && !data && <Spinner />}
      {error && <ErrorBox message={error} onRetry={reload} />}

      <ul className="sources__list">
        {data?.map((source) => (
          <SourceRow key={source.id} source={source} onChanged={reload} />
        ))}
      </ul>

      <section className="sources__add">
        <h2>Kaynak ekle</h2>

        <div className="tabs">
          <button
            type="button"
            className={draft.type === 'xtream' ? 'active' : ''}
            onClick={() => {
              setDraft(emptyXtream);
              setTest(null);
            }}
          >
            Xtream Codes
          </button>
          <button
            type="button"
            className={draft.type === 'm3u' ? 'active' : ''}
            onClick={() => {
              setDraft(emptyM3u);
              setTest(null);
            }}
          >
            M3U
          </button>
        </div>

        <form onSubmit={runTest}>
          <label>
            Ad
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              required
              maxLength={60}
            />
          </label>

          {draft.type === 'xtream' ? (
            <>
              <label>
                Sunucu adresi
                <input
                  value={draft.baseUrl}
                  onChange={(e) => setDraft({ ...draft, baseUrl: e.target.value })}
                  placeholder="http://panel.example:8080"
                  required
                />
              </label>
              <label>
                Kullanıcı adı
                <input
                  value={draft.username}
                  onChange={(e) => setDraft({ ...draft, username: e.target.value })}
                  autoComplete="off"
                  required
                />
              </label>
              <label>
                Parola
                <input
                  type="password"
                  value={draft.password}
                  onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                  autoComplete="off"
                  required
                />
              </label>
            </>
          ) : (
            <label>
              M3U adresi
              <input
                value={draft.m3uUrl}
                onChange={(e) => setDraft({ ...draft, m3uUrl: e.target.value })}
                placeholder="http://panel.example/get.php?username=…"
                required
              />
            </label>
          )}

          <div className="formrow">
            <button type="submit" disabled={busy !== null}>
              {busy === 'test' ? 'Deneniyor…' : 'Bağlantıyı test et'}
            </button>
            <button type="button" onClick={() => void save()} disabled={busy !== null || !test?.ok}>
              {busy === 'save' ? 'Kaydediliyor…' : 'Kaydet'}
            </button>
          </div>
        </form>

        {formError && <ErrorBox message={formError} />}
        {test && <TestResult result={test} />}
      </section>
    </main>
  );
}

function TestResult({ result }: { result: SourceTestResult }) {
  return (
    <div className={result.ok ? 'test test--ok' : 'test test--fail'}>
      <strong>{result.ok ? 'Bağlantı başarılı' : 'Bağlantı başarısız'}</strong>
      <p>{result.message}</p>
      {result.expiresAt && <p>Abonelik bitişi: {new Date(result.expiresAt).toLocaleDateString('tr')}</p>}
      {result.maxConnections != null && (
        <p>
          Eşzamanlı bağlantı: {result.activeConnections ?? 0} / {result.maxConnections}
        </p>
      )}
      {result.counts && (
        <p>
          Canlı {result.counts.live} · Film {result.counts.movie} · Dizi {result.counts.series}
        </p>
      )}
      {result.formatProbe && (
        <p>
          Tespit edilen format: <code>{result.formatProbe.chosen ?? 'bulunamadı'}</code>
          {/* The per-item probe can still override this; it is a default, not a promise. */}
        </p>
      )}
    </div>
  );
}

function SourceRow({ source, onChanged }: { source: SourceDto; onChanged: () => void }) {
  const [state, setState] = useState<SyncState>(source.syncState);
  const [busy, setBusy] = useState(false);
  const poll = useRef<ReturnType<typeof setInterval> | null>(null);
  const { confirm, dialog } = useConfirm();

  // Poll only while something is actually running, so an idle settings page makes no
  // requests at all.
  useEffect(() => {
    const running = state.phase === 'running' || state.phase === 'queued';
    if (!running) {
      if (poll.current) clearInterval(poll.current);
      poll.current = null;
      return;
    }
    poll.current = setInterval(() => {
      void api<SyncState>(`/api/sources/${source.id}/sync-status`)
        .then((next) => {
          setState(next);
          if (next.phase === 'done') onChanged();
        })
        .catch(() => {});
    }, 1500);

    return () => {
      if (poll.current) clearInterval(poll.current);
    };
  }, [state.phase, source.id, onChanged]);

  async function sync() {
    setBusy(true);
    try {
      const res = await api<{ syncState: SyncState }>(`/api/sources/${source.id}/sync`, {
        method: 'POST',
      });
      setState({ ...res.syncState, phase: 'running' });
    } catch (err) {
      setState({ ...state, phase: 'failed', error: err instanceof Error ? err.message : 'Hata' });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    const ok = await confirm({
      title: `“${source.name}” silinsin mi?`,
      body: 'Kaynakla birlikte ondan içe aktarılan tüm katalog da kaldırılır. Kayıtlı şifre sunucudan silinir; geri almak için baştan girmeniz gerekir.',
      confirmLabel: 'Sil',
      danger: true,
    });
    if (!ok) return;
    await api(`/api/sources/${source.id}`, { method: 'DELETE' });
    onChanged();
  }

  const running = state.phase === 'running' || state.phase === 'queued';

  return (
    <li className="source">
      <div>
        <strong>{source.name}</strong>
        <span className="source__meta">
          {source.type === 'xtream' ? 'Xtream' : 'M3U'} · {source.host ?? '—'}
          {source.liveFormat && ` · ${source.liveFormat}`}
        </span>
        {state.phase === 'done' && (
          <span className="source__meta">
            Canlı {state.counts.live} · Film {state.counts.movie} · Dizi {state.counts.series}
          </span>
        )}
        {running && (
          <span className="source__meta">
            İçe aktarılıyor{state.currentKind ? ` (${state.currentKind})` : ''} — {state.processed} öğe
          </span>
        )}
        {state.phase === 'failed' && <span className="error">{state.error}</span>}

        {/*
          Information, not a gate. The panel reports this number once, at test time, and
          nobody reads a panel — so it is shown here. What to do about it is the operator's
          call; ENFORCE_PROVIDER_LIMIT is what makes it binding.
        */}
        {source.maxConnections != null && (
          <span className="source__quota">
            Sağlayıcı kaydına göre abonelik <strong>{source.maxConnections}</strong> eşzamanlı
            yayın için tanımlı. Bilgi amaçlı — uygulama kendi limitine göre çalışıyor.
          </span>
        )}
        {source.accountExpiresAt && (
          <span className="source__meta">
            Abonelik bitişi: {new Date(source.accountExpiresAt).toLocaleDateString('tr-TR')}
          </span>
        )}
      </div>

      <div className="formrow">
        <button type="button" onClick={() => void sync()} disabled={busy || running}>
          {running ? 'Sürüyor…' : 'Senkronize et'}
        </button>
        <button type="button" className="danger" onClick={() => void remove()} disabled={running}>
          Sil
        </button>
      </div>

      {dialog}
    </li>
  );
}
